import type { ScrapingQueueItem } from '@repo/types';

/**
 * Source-platform detection + presentation for scraping-queue rows. The queue
 * stores only a `sourceUrl`, so the platform and a human title are derived from
 * it (and from any scraped data) for the table UI.
 */

export interface PlatformMeta {
  key: string;
  label: string;
  /** Badge classes — subtle, brand-ish, theme-aware (matches the status badges). */
  className: string;
}

const PLATFORMS: (PlatformMeta & { test: RegExp })[] = [
  { key: 'linkedin', label: 'LinkedIn', test: /linkedin\./i, className: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-400' },
  { key: 'indeed', label: 'Indeed', test: /indeed\./i, className: 'border-indigo-500/20 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400' },
  { key: 'ziprecruiter', label: 'ZipRecruiter', test: /ziprecruiter\./i, className: 'border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400' },
  { key: 'naukri', label: 'Naukri', test: /naukri\./i, className: 'border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  { key: 'bayt', label: 'Bayt', test: /bayt\./i, className: 'border-teal-500/20 bg-teal-500/10 text-teal-700 dark:text-teal-400' },
  { key: 'glassdoor', label: 'Glassdoor', test: /glassdoor\./i, className: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
];

const OTHER: PlatformMeta = {
  key: 'other',
  label: 'Web',
  className: 'border-border bg-muted text-muted-foreground',
};

export function detectPlatform(url: string): PlatformMeta {
  const match = PLATFORMS.find((p) => p.test.test(url));
  if (match) return match;
  try {
    return { ...OTHER, label: new URL(url).hostname.replace(/^www\./, '') };
  } catch {
    return OTHER;
  }
}

/** hostname + path, without protocol/www or a trailing slash — a compact URL. */
export function prettySourceUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.hostname.replace(/^www\./, '')}${u.pathname}`.replace(/\/$/, '');
  } catch {
    return url;
  }
}

const GENERIC_SLUGS = new Set(['viewjob', 'jobs', 'job', 'search', 'apply', 'listing']);

/**
 * A human title for the row: prefer scraped/form data, otherwise humanize the
 * URL slug (strips the leading/trailing numeric id job boards append). Returns
 * null when nothing readable can be derived (e.g. Indeed's `viewjob?jk=…`), so
 * the caller can fall back to the URL.
 */
export function deriveJobTitle(item: ScrapingQueueItem): string | null {
  const known = (item.scrapedData?.title || item.formInputData?.title || '').trim();
  if (known) return known;

  try {
    const u = new URL(item.sourceUrl);
    const last = u.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop() ?? '';
    const slug = decodeURIComponent(last)
      .replace(/\.(html?|php|aspx?)$/i, '')
      .replace(/^job-listings-/i, '')
      .replace(/^\d+-/, '') // leading numeric id (ZipRecruiter)
      .replace(/-\d+$/, '') // trailing numeric id (Bayt / Naukri)
      .replace(/[-_]+/g, ' ')
      .trim();

    if (slug.length >= 3 && !GENERIC_SLUGS.has(slug.toLowerCase()) && /[a-z]{3}/i.test(slug)) {
      return slug.replace(/\b\w/g, (c) => c.toUpperCase());
    }
  } catch {
    /* not a parseable URL */
  }
  return null;
}
